import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';

import LocalStorageItem from './LocalStorageItem';
import { ILocalStorageConfiguration, LocalStorageItemKeys } from './LocalStorageManager.types';

/**
 * Class responsible for saving and reading data from window.localStorage object.
 */
class LocalStorageManager {
    private supported: boolean = false;
    private gameId: string = '';
    private localGameKeys: Array<LocalStorageItemKeys> = [
        LocalStorageItemKeys.GAME_BET,
        LocalStorageItemKeys.GAME_PLAY_BACKGROUND_SOUND,
        LocalStorageItemKeys.GAME_PLAY_IN_AUTO_MODE,
        LocalStorageItemKeys.SHOW_PRELOADER_INFO,
        LocalStorageItemKeys.SHOW_INTRO_MOVIE,
        LocalStorageItemKeys.GAME_AUTO_SETTINGS,
        LocalStorageItemKeys.GAME_SOUNDS_SETTINGS,
        LocalStorageItemKeys.GAME_GAMBLE_SETTINGS,
        LocalStorageItemKeys.WCA_MODE,
        LocalStorageItemKeys.LOCAL_FIXES,
        LocalStorageItemKeys.LEFT_HAND_MODE
    ];

    private localGlobalKeys: Array<LocalStorageItemKeys> = [LocalStorageItemKeys.GLOBAL_VOLUME];

    public constructor() {
        //Logger.log(LoggerLevel.Constructor, { message: 'LocalStorageManager constructor.', source: LoggerSource.Ext });
    }

    public init(gameId: string): void {
        this.gameId = gameId;

        try {
            const key: string = 'checkKey';
            localStorage.setItem(key, key);
            localStorage.removeItem(key);
            this.supported = true;
        } catch (e) {
            Logger.log(LoggerLevel.Debug, {
                class: 'LocalStorageManager',
                fn: 'init',
                message: `Could not use Storage as it is not available.`,
                source: LoggerSource.Ext
            });
            this.supported = false;
        }
    }

    public isSupported(): boolean {
        return this.supported;
    }

    public saveItem(item: LocalStorageItem) {
        if (this.isSupported()) {
            const mode = this.getAcceptableGameSpecificItems(item.key) ? 1 : this.getAcceptableGlobalItems(item.key) ? 2 : 3;
            switch (mode) {
                case 1: {
                    let gameProperties: any = localStorage[this.gameId];
                    if (!gameProperties) {
                        gameProperties = {};
                    } else {
                        gameProperties = JSON.parse(gameProperties);
                    }
                    gameProperties[item.key] = item.value;
                    localStorage[this.gameId] = JSON.stringify(gameProperties);
                    break;
                }
                case 2:
                    localStorage.setItem(item.key, item.value);
                    break;
                case 3:
                    Logger.log(LoggerLevel.Debug, {
                        class: 'LocalStorageManager',
                        fn: 'saveItem',
                        message: `Not acceptable item '${item.key}' = '${item.value}' could not be stored in 'localStorage'.`,
                        source: LoggerSource.Ext
                    });
                    break;
                default:
                    break;
            }
        }
    }

    public readItem<K extends LocalStorageItemKeys>(itemKey: K): ILocalStorageConfiguration[K] | null {
        if (!this.isSupported) return null;

        const itemValue = localStorage.getItem(this.gameId);
        let returnValue = null;

        if (itemValue !== null) {
            let parsedGameObj: ILocalStorageConfiguration = JSON.parse(itemValue);

            returnValue = parsedGameObj[itemKey];

            if (typeof returnValue === 'string') returnValue = JSON.parse(returnValue);
        }

        return returnValue;
    }

    public removeItem(itemKey: string): void {
        const itemValue: string | null = localStorage.getItem(this.gameId);

        if (itemValue !== null) {
            let parsedGameObj = JSON.parse(itemValue);
            delete parsedGameObj[itemKey];
            localStorage[this.gameId] = JSON.stringify(parsedGameObj);
        }
    }

    public removeAllItems(): void {
        //localStorage?.clear();
        localStorage.removeItem(this.gameId);
    }

    private getAcceptableGlobalItems(key: LocalStorageItemKeys): boolean {
        return this.localGlobalKeys.includes(key);
    }

    private getAcceptableGameSpecificItems(key: LocalStorageItemKeys): boolean {
        return this.localGameKeys.includes(key);
    }
}

export default new LocalStorageManager();
